import { useEffect, useRef, useState } from 'react'
import { TriangleAlert } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel, FieldSeparator } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import ConditionEditor from '../../components/ConditionEditor'
import type { FieldType, FormDefinition, FormField } from '../../types'
import OptionsEditor from './OptionsEditor'
import { changeFieldType, FIELD_TYPES, isSelectType, KEY_RE, precedingFields, TEXT_TYPES, TYPE_LABEL } from './model'
import { TYPE_ICON } from './typeMeta'

interface Props {
    def: FormDefinition
    pageId: string
    index: number
    field: FormField
    /** Keys present in the currently published version (for the rename warning). */
    publishedKeys: Set<string>
    publishedVersion?: string
    /** Increments when a field was just added, to move focus to the label. */
    focusToken: number
    onPatch: (patch: Partial<FormField>) => void
    /** Replace the whole field (used by type changes, which drop properties). */
    onReplace: (field: FormField) => void
    onLabel: (label: string) => void
    onKey: (key: string) => void
}

export default function FieldInspector({ def, pageId, index, field, publishedKeys, publishedVersion, focusToken, onPatch, onReplace, onLabel, onKey }: Props) {
    const labelRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
    const [pendingType, setPendingType] = useState<{ type: FieldType; lost: string[] } | null>(null)

    useEffect(() => {
        if (focusToken > 0) labelRef.current?.focus()
    }, [focusToken])

    const page = def.pages.find((p) => p.id === pageId)
    const isHtml = field.type === 'html'
    const sameKey = page?.fields.filter((f, i) => i !== index && f.key === field.key).length ?? 0
    const conflict = def.pages.some((p) => p.fields.some((f, i) => !(p.id === pageId && i === index) && f.key === field.key && f.type !== field.type))
    const keyError = !KEY_RE.test(field.key)
        ? 'Use lowercase letters, digits and underscores, starting with a letter (max 64).'
        : sameKey > 0
            ? 'Another field on this page already uses this key.'
            : conflict
                ? 'This key is used elsewhere with a different type.'
                : null
    const labelError = !isHtml && field.label.trim() === '' ? 'A label is required.' : null
    const inPublished = publishedKeys.has(field.key)

    const requestType = (t: string) => {
        const type = t as FieldType
        if (type === field.type) return
        const { lost } = changeFieldType(field, type)
        if (lost.length > 0) setPendingType({ type, lost })
        else onReplace(changeFieldType(field, type).field)
    }
    const confirmType = () => {
        if (pendingType) onReplace(changeFieldType(field, pendingType.type).field)
        setPendingType(null)
    }
    const Icon = TYPE_ICON[field.type]
    const options = field.options ?? []
    const preceding = precedingFields(def, pageId, index)

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary"><Icon data-icon="inline-start" /> {TYPE_LABEL[field.type]}</Badge>
                {field.stableId && <Badge variant="outline" className="font-mono" title="Stable id (read-only)">{field.stableId}</Badge>}
            </div>
            <FieldGroup>
                <Field data-invalid={!!labelError || undefined}>
                    <FieldLabel htmlFor="fi-label">{isHtml ? 'Content (markdown allowed)' : 'Label'}</FieldLabel>
                    {isHtml ? (
                        <Textarea
                            id="fi-label" rows={4} ref={(el) => { labelRef.current = el }}
                            value={field.label} onChange={(e) => onLabel(e.target.value)}
                        />
                    ) : (
                        <Input
                            id="fi-label" ref={(el) => { labelRef.current = el }}
                            value={field.label} aria-invalid={!!labelError || undefined}
                            placeholder="The question people will see"
                            onChange={(e) => onLabel(e.target.value)}
                        />
                    )}
                    {labelError && <FieldError>{labelError}</FieldError>}
                </Field>

                <Field data-invalid={!!keyError || undefined}>
                    <FieldLabel htmlFor="fi-key">Key</FieldLabel>
                    <Input
                        id="fi-key" className="font-mono text-xs" value={field.key}
                        aria-invalid={!!keyError || undefined} onChange={(e) => onKey(e.target.value)}
                    />
                    {keyError
                        ? <FieldError>{keyError}</FieldError>
                        : <FieldDescription>Identifies this answer in conditions, rules and reports.</FieldDescription>}
                    {inPublished && (
                        <Alert>
                            <TriangleAlert />
                            <AlertTitle>In the published version</AlertTitle>
                            <AlertDescription>
                                {publishedVersion ? `Version ${publishedVersion} uses` : 'The published version uses'} this key. Renaming it detaches answers already collected under it.
                            </AlertDescription>
                        </Alert>
                    )}
                </Field>

                <Field>
                    <FieldLabel htmlFor="fi-type">Type</FieldLabel>
                    <Select value={field.type} onValueChange={requestType}>
                        <SelectTrigger id="fi-type" className="w-full"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectGroup>
                            {FIELD_TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}
                        </SelectGroup></SelectContent>
                    </Select>
                </Field>

                {!isHtml && (
                    <Field orientation="horizontal">
                        <Switch id="fi-required" checked={!!field.required} onCheckedChange={(v) => onPatch({ required: v || undefined })} />
                        <FieldLabel htmlFor="fi-required" className="font-normal">Required</FieldLabel>
                    </Field>
                )}
                <Field orientation="horizontal">
                    <Switch id="fi-hidden" checked={!!field.hidden} onCheckedChange={(v) => onPatch({ hidden: v || undefined })} />
                    <FieldLabel htmlFor="fi-hidden" className="font-normal">Hidden (never shown to respondents)</FieldLabel>
                </Field>

                {!isHtml && (
                    <Field>
                        <FieldLabel htmlFor="fi-help">Help text</FieldLabel>
                        <Textarea id="fi-help" rows={2} value={field.help ?? ''} onChange={(e) => onPatch({ help: e.target.value || undefined })} />
                        <FieldDescription>Markdown is allowed.</FieldDescription>
                    </Field>
                )}

                {TEXT_TYPES.includes(field.type) && (
                    <Field>
                        <FieldLabel htmlFor="fi-max">Max length</FieldLabel>
                        <Input
                            id="fi-max" type="number" min={1} className="max-w-40"
                            value={field.maxLength ?? ''}
                            onChange={(e) => onPatch({ maxLength: e.target.value === '' ? undefined : Math.max(1, Math.floor(Number(e.target.value))) })}
                        />
                    </Field>
                )}

                {isSelectType(field.type) && (
                    <OptionsEditor key={`${pageId}:${index}`} options={options} onChange={(o) => onPatch({ options: o })} />
                )}

                <FieldSeparator />

                <Field>
                    <FieldLabel>Visible when</FieldLabel>
                    <FieldDescription>Only answers from earlier questions can be used.</FieldDescription>
                    {preceding.length === 0 && !field.visibleWhen ? (
                        <p className="text-sm text-muted-foreground">No earlier questions to depend on yet. Always visible.</p>
                    ) : (
                        <ConditionEditor value={field.visibleWhen} onChange={(c) => onPatch({ visibleWhen: c })} fields={preceding} emptyText="Always visible" />
                    )}
                </Field>
            </FieldGroup>

            <Dialog open={pendingType !== null} onOpenChange={(o) => { if (!o) setPendingType(null) }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Change type to {pendingType ? TYPE_LABEL[pendingType.type] : ''}?</DialogTitle>
                        <DialogDescription>This will remove {pendingType?.lost.join(' and ')} from this field.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPendingType(null)}>Cancel</Button>
                        <Button onClick={confirmType}>Change type</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
